import { useState, useEffect, useCallback } from 'react';
import { useOperacionesCache } from '../contexts/OperacionesCacheContext';
import { iniciativasAPI } from '../services/api';

/**
 * Hook personalizado para manejar la carga de iniciativas con caché
 * @param {Object} options - Opciones de configuración
 * @param {boolean} options.autoLoad - Si cargar automáticamente al montar (default: true)
 * @param {string[]} options.estadosFiltro - Estados a filtrar (default: estados operacionales)
 * @param {number} options.cacheExpirationMinutes - Minutos antes de que expire el caché (default: 30)
 * @param {string} options.endpoint - Endpoint a usar: 'all' | 'operaciones' | 'desarrollo_negocio' (default: 'all')
 */
export const useIniciativasWithCache = (options = {}) => {
  const {
    autoLoad = true,
    estadosFiltro = ['aprobado', 'planificada', 'en_curso', 'bloqueada', 'cancelada', 'finalizada'],
    cacheExpirationMinutes = 30,
    endpoint = 'all'
  } = options;

  const { 
    getCacheData,
    updateCache, 
    setLoading: setCacheLoading, 
    clearCache, 
    hasValidCache, 
    isCacheExpired 
  } = useOperacionesCache();

  // Obtener datos de caché específicos del endpoint
  const cacheData = getCacheData(endpoint);
  
  const [iniciativas, setIniciativas] = useState([]);
  const [loading, setLoading] = useState(!hasValidCache(endpoint));
  const [isLoadingRunning, setIsLoadingRunning] = useState(false);
  const [error, setError] = useState(null);

  // Efecto para cargar datos del caché cuando estén disponibles
  useEffect(() => {
    if (hasValidCache(endpoint) && !isCacheExpired(endpoint)) {
      setIniciativas(cacheData.iniciativas || []);
      setLoading(false);
      setError(cacheData.error);
    }
  }, [cacheData, hasValidCache, isCacheExpired, endpoint]);

  // Función principal para cargar iniciativas
  const loadIniciativas = useCallback(async (forceReload = false) => {
    // Evitar llamadas múltiples simultáneas
    if (isLoadingRunning) {
      return { success: false, message: 'Ya hay una carga en progreso' };
    }
    
    // Si tenemos datos válidos en caché y no es una recarga forzada, usar el caché
    if (!forceReload && hasValidCache(endpoint) && !isCacheExpired(endpoint)) {
      const cachedData = getCacheData(endpoint);
      setIniciativas(cachedData.iniciativas || []);
      setLoading(false);
      setError(cachedData.error);
      return { success: true, source: 'cache', count: cachedData.iniciativas?.length || 0 };
    }
    
    setIsLoadingRunning(true);
    setCacheLoading(true, endpoint);
    setError(null);
    
    try {
      setLoading(true);
      console.log(`Cargando iniciativas desde el servidor (endpoint: ${endpoint})...`);
      
      // Seleccionar endpoint según la opción
      let response;
      if (endpoint === 'operaciones') {
        response = await iniciativasAPI.getTablaOperaciones();
      } else if (endpoint === 'desarrollo_negocio') {
        response = await iniciativasAPI.getTablaDesarrolloNegocio();
      } else {
        response = await iniciativasAPI.getAllWithPersonas();
      }
      
      const iniciativasData = response.data.results || response.data;
      
      if (!Array.isArray(iniciativasData)) {
        throw new Error('Formato de datos inválido recibido del servidor');
      }
      
      // Filtrar por estados si se especificaron
      const iniciativasFiltradas = estadosFiltro.length > 0 
        ? iniciativasData.filter(iniciativa => estadosFiltro.includes(iniciativa.estado))
        : iniciativasData;
      
      const queryTime = response.data.query_time || 'N/A';
      console.log(`Cargadas ${iniciativasFiltradas.length} iniciativas desde el servidor en ${queryTime}`);
      
      // Actualizar el caché y el estado local
      updateCache(iniciativasFiltradas, null, endpoint);
      setIniciativas(iniciativasFiltradas);
      
      return { 
        success: true, 
        source: 'server', 
        count: iniciativasFiltradas.length,
        message: `Cargadas ${iniciativasFiltradas.length} iniciativas`,
        queryTime
      };
      
    } catch (err) {
      console.error('Error al cargar iniciativas:', err);
      const errorMessage = err.message || 'Error desconocido al cargar las iniciativas';
      
      // Actualizar el caché con el error
      updateCache([], err, endpoint);
      setIniciativas([]);
      setError(errorMessage);
      
      return { 
        success: false, 
        error: errorMessage,
        message: 'Error al cargar las iniciativas'
      };
    } finally {
      setLoading(false);
      setIsLoadingRunning(false);
    }
  }, [
    hasValidCache, 
    isCacheExpired, 
    getCacheData,
    updateCache, 
    setCacheLoading, 
    isLoadingRunning,
    estadosFiltro,
    endpoint
  ]);

  // Función para refrescar datos (con mensaje de confirmación)
  const refreshData = useCallback(async () => {
    console.log('Refrescando datos de iniciativas...');
    clearCache(endpoint);
    return await loadIniciativas(true);
  }, [loadIniciativas, clearCache, endpoint]);

  // Función para obtener estadísticas del caché
  const getCacheStats = useCallback(() => {
    if (!hasValidCache(endpoint)) {
      return { hasCache: false };
    }

    const cachedData = getCacheData(endpoint);
    const ageInMinutes = Math.floor((Date.now() - cachedData.lastFetched) / (1000 * 60));
    const isExpired = ageInMinutes >= cacheExpirationMinutes;
    
    return {
      hasCache: true,
      count: cachedData.iniciativas?.length || 0,
      lastFetched: new Date(cachedData.lastFetched),
      ageInMinutes,
      isExpired,
      expiresIn: Math.max(0, cacheExpirationMinutes - ageInMinutes)
    };
  }, [hasValidCache, getCacheData, cacheExpirationMinutes, endpoint]);

  // Auto-carga inicial
  useEffect(() => {
    if (autoLoad && (!hasValidCache(endpoint) || isCacheExpired(endpoint))) {
      loadIniciativas();
    }
  }, [autoLoad, hasValidCache, isCacheExpired, loadIniciativas, endpoint]);

  return {
    iniciativas,
    loading,
    error,
    loadIniciativas,
    refreshData,
    clearCache: () => clearCache(endpoint),
    getCacheStats,
    hasValidCache: hasValidCache(endpoint),
    isCacheExpired: isCacheExpired(endpoint)
  };
};